import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled, requireListingImage } from '../../../util/configHelpers';
import { getCountryFlagById } from '../../../util/getCountryFlag';
import { languagesHelper as language } from '../../../util/languages';

import {
  AspectRatioWrapper,
  ResponsiveImage,
  ListingCardThumbnail,
  AddToFavouritesButton,
  IconButtonArrow,
  IconLandscapes,
  ReviewRatingCustom,
} from '../../../components';

import css from './SearchMapInfoCard.module.css';

// ListingCard is the listing info without overlayview or carousel controls
const ListingCard = props => {
  const {
    className,
    clickHandler,
    intl,
    isInCarousel,
    listing,
    urlToListing,
    config,
  } = props;

  const { title, price, publicData } = listing.attributes;
  const { listingType, cardStyle, linstingFieldId_country, categoryLevel1, field_duration } = publicData || {};
  const formattedPrice =
    price && price.currency === config.currency
      ? formatMoney(intl, price)
      : price?.currency
        ? price.currency
        : null;
  const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const pricePerUnit = intl.formatMessage(
    { id: 'SearchMapInfoCard.perUnit' },
    { unitType: publicData?.unitType }
  );
  const priceValue = formattedPrice ? formattedPrice : '';

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === publicData?.listingType
  );
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isNegotiationRegularListingType =
    foundListingTypeConfig?.listingType === 'negotiation-type-regular';

  const isExperience = categoryLevel1 === "category-tours" || categoryLevel1 === "category-experiences";
  const rating = publicData?.currentRating;
  const field_landscape = publicData?.field_landscape;

  const from = (
    <span className={css.from}>
      {intl.formatMessage({ id: 'SearchMapInfoCard.priceFrom' })}
    </span>
  );

  const priceMessage =
    hasMultiplePriceVariants || (isNegotiationRegularListingType && price)
      ? intl.formatMessage(
        { id: 'SearchMapInfoCard.priceStartingFrom' },
        { from, priceValue: <span className={css.priceValue}>{priceValue}</span>, pricePerUnit }
      )
      : isNegotiationRegularListingType && !price
        ? <span className={css.priceValue}>{intl.formatMessage({ id: 'SearchMapInfoCard.priceOnRequest' })}</span>
        : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue: <span className={css.priceValue}>{priceValue}</span>, pricePerUnit });

  // listing card anchor needs sometimes inherited border radius.
  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  return (
    <a
      alt={title}
      className={classes}
      href={urlToListing}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => {
        e.preventDefault();
        // Use clickHandler from props to call internal router
        clickHandler(listing);
      }}
    >
      <div
        className={classNames(css.card, css.borderRadiusInheritTop, {
          [css.borderRadiusInheritBottom]: !isInCarousel,
        })}
      >
        <div className={css.imageHolder}>
          {/* <AddToFavouritesButton
            listing={listing}
            className={css.addToFavourites}
          /> */}
          {!!linstingFieldId_country && <span className={css.flag}>
            {getCountryFlagById(linstingFieldId_country)}
            {language.labelsTranslator(linstingFieldId_country, intl)}
          </span>}
          {showListingImage ? (
            <AspectRatioWrapper
              className={css.aspectRatioWrapper}
              width={aspectWidth}
              height={aspectHeight}
            >
              <ResponsiveImage
                rootClassName={classNames(css.rootForImage, css.borderRadiusInheritTop)}
                alt={title}
                noImageMessage={intl.formatMessage({ id: 'SearchMapInfoCard.noImage' })}
                image={firstImage}
                variants={variants}
                sizes="250px"
              />
            </AspectRatioWrapper>
          ) : (
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={title}
              className={css.aspectRatioWrapper}
              width={aspectWidth}
              height={aspectHeight}
            />
          )}

          {field_landscape && field_landscape !== "not-active-in-my-area" && (
            <div className={classNames(css.landscape, {[css.landscapeWide]: field_landscape === "no-but-i-am-a-member-of-ERN"})}>
              <IconLandscapes type={field_landscape} />
            </div>
          )}
        </div>
        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.category}>
              {language.labelsTranslator(categoryLevel1, intl)} {isExperience &&
                intl.formatMessage({
                  id: 'ListingCard.experience'
                })}
            </div>
            <div className={css.title}>
              {title}
            </div>

            {/* {field_duration && (
              <div className={css.duration}>
                {field_duration}
              </div>
            )} */}
          </div>

          {rating && (
            <div className={css.rating}>
              <ReviewRatingCustom rating={rating} className={css.ratingStars} />
              <span className={css.ratingTotal}>
                {rating}
              </span>
            </div>
          )}

          <div className={css.priceInfo}>
            <div className={classNames(css.price, { [css.noPriceSetLabel]: !formattedPrice })}>
              {priceMessage}
            </div>

            <span className={css.seeDetails}>
              <IconButtonArrow />
              <FormattedMessage id="ListingCard.seeDetails" />
            </span>

          </div>
        </div>
      </div>
    </a>
  );
};

/**
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {Function} props.onListingInfoCardClicked - The function to handle the listing info card click
 * @param {Function} props.createURLToListing - The function to create the URL to the listing
 * @param {Object} props.config - The configuration
 * @returns {JSX.Element}
 */
const SearchMapInfoCard = props => {
  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const intl = useIntl();
  const {
    className,
    rootClassName,
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    config,
  } = props;
  const currentListing = ensureListing(listings[currentListingIndex]);
  const hasCarousel = listings.length > 1;

  const classes = classNames(rootClassName || css.root, className);
  const caretClass = classNames(css.caret, { [css.caretWithCarousel]: hasCarousel });

  return (
    <div className={classes}>
      <div className={css.caretShadow} />
      <ListingCard
        clickHandler={onListingInfoCardClicked}
        urlToListing={createURLToListing(currentListing)}
        listing={currentListing}
        intl={intl}
        isInCarousel={hasCarousel}
        config={config}
      />
      {hasCarousel ? (
        <div className={classNames(css.paginationInfo, css.borderRadiusInheritBottom)}>
          <button
            className={css.paginationPrev}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length - 1) % listings.length
              );
            }}
          />
          <div className={css.paginationPage}>
            {`${currentListingIndex + 1}/${listings.length}`}
          </div>
          <button
            className={css.paginationNext}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length + 1) % listings.length
              );
            }}
          />
        </div>
      ) : null}
      {/* <div className={caretClass} /> */}
    </div>
  );
};

export default SearchMapInfoCard;
